import {
  DataGrid,
  FilterToolbar,
  StatusBadge,
  type Severity
} from '@ijoin/components';
import {
  DriftFinding,
  DriftFindingSeverity,
  useGetAdminDbInsightsDrift
} from '@ijoin/hooks';
import { Alert, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import { camelCase } from 'lodash';
import { FC } from 'react';

import { LinkButton } from './LinkButton';

const DRIFT_SCRIPT_URL = '/api/v3/admin/db-insights/drift/script';

const SEVERITY_LABELS: Record<DriftFindingSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  low: 'Low',
  medium: 'Medium'
};

const SEVERITY_BADGES: Record<DriftFindingSeverity, Severity> = {
  critical: 'error',
  high: 'error',
  low: 'info',
  medium: 'warning'
};

const KIND_LABELS: Record<string, string> = {
  'column.mismatch': 'Column differs',
  'column.missing': 'Column missing',
  'column.unexpected': 'Column not in baseline',
  'foreignKey.mismatch': 'Foreign key differs',
  'foreignKey.missing': 'Foreign key missing',
  'foreignKey.unexpected': 'Foreign key not in baseline',
  'index.mismatch': 'Index differs',
  'index.missing': 'Index missing',
  'index.unexpected': 'Index not in baseline',
  'table.collationMismatch': 'Collation differs',
  'table.engineMismatch': 'Engine differs',
  'table.missing': 'Table missing',
  'table.unexpected': 'Table not in baseline',
  'view.mismatch': 'View differs',
  'view.missing': 'View missing',
  'view.unexpected': 'View not in baseline'
};

const SEVERITY_ORDER: DriftFindingSeverity[] = [
  'critical',
  'high',
  'medium',
  'low'
];

export const SchemaDriftTab: FC = () => {
  const drift = useGetAdminDbInsightsDrift();
  const findings = drift.data?.findings ?? [];

  const onDownloadScript = () => window.open(DRIFT_SCRIPT_URL, '_blank');

  const columns: GridColDef<DriftFinding>[] = [
    {
      field: 'severity',
      headerName: 'Severity',
      renderCell: params => (
        <StatusBadge severity={SEVERITY_BADGES[params.row.severity]}>
          {SEVERITY_LABELS[params.row.severity]}
        </StatusBadge>
      ),
      width: 110
    },
    {
      field: 'table',
      flex: 1.2,
      headerName: 'Table',
      renderCell: params => (
        <LinkButton
          data-component='link'
          data-testid={camelCase(params.row.table)}
          params={{ table: params.row.table }}
          to='/db-insights/tables/$table'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      field: 'kind',
      flex: 1,
      headerName: 'Issue',
      valueGetter: (_value, row) => KIND_LABELS[row.kind] ?? row.kind
    },
    {
      field: 'name',
      flex: 1.2,
      headerName: 'Object'
    },
    {
      field: 'expected',
      flex: 1.4,
      headerName: 'Expected'
    },
    {
      field: 'found',
      flex: 1.4,
      headerName: 'Found'
    }
  ];

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      {drift.data && !drift.data.versionMatch && (
        <Alert data-testid='driftVersionWarning' severity='warning'>
          The baseline was captured at Flyway V
          {drift.data.baseline.flywayVersion} but this database is at V
          {drift.data.live.flywayVersion}. Differences below may just be
          migrations this environment has not run yet, so treat the findings as
          unreliable until the versions agree.
        </Alert>
      )}

      {drift.data && drift.data.summary.total === 0 && (
        <Alert data-testid='driftClean' severity='success'>
          This database matches the embedded baseline exactly.
        </Alert>
      )}

      <Stack alignItems='center' direction='row' spacing={1}>
        {SEVERITY_ORDER.map(severity => (
          <span data-testid={`driftCount-${severity}`} key={severity}>
            <StatusBadge severity={SEVERITY_BADGES[severity]}>
              {`${SEVERITY_LABELS[severity]}: ${drift.data?.summary[severity] ?? 0}`}
            </StatusBadge>
          </span>
        ))}

        {drift.data && (
          <Typography color='text.secondary' variant='body2'>
            baseline V{drift.data.baseline.flywayVersion} vs{' '}
            {drift.data.live.database} V{drift.data.live.flywayVersion}
          </Typography>
        )}
      </Stack>

      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={row => `${row.table}.${row.kind}.${row.name ?? ''}`}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 25 } }
        }}
        loading={drift.isFetching}
        pageSizeOptions={[15, 25, 50, 100]}
        rows={findings}
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'schemaDrift'
          },
          toolbar: {
            downloadTestId: 'downloadDriftScript',
            /* nothing to reconcile means nothing to download */
            onDownload: findings.length ? onDownloadScript : undefined,
            onRefresh: drift.refetch,
            showQuickFilter: true
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Stack>
  );
};

SchemaDriftTab.displayName = 'SchemaDriftTab';
